// Collapsed/expanded state for the app rail.
//
// It lives outside React because the rail is now on every signed-in page: if
// each page owned its own useState, the rail would silently re-expand on every
// navigation. One module-level value + localStorage keeps it put across routes
// AND across sessions, and the subscriber list keeps every mounted rail in step
// (the Studio collapses it programmatically when a track loads).
import { useEffect, useState } from 'react';

const KEY = 'rl_rail_collapsed_v1';

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false; // private mode / quota — default to the roomier state
  }
}

let collapsed = read();
const subs = new Set<(v: boolean) => void>();

export function setRailCollapsed(v: boolean) {
  if (v === collapsed) return;
  collapsed = v;
  try { localStorage.setItem(KEY, v ? '1' : '0'); } catch { /* ignore */ }
  subs.forEach((fn) => fn(v));
}

/** `[collapsed, setCollapsed]`, shared by every rail on the page. */
export function useRailCollapsed(): [boolean, (v: boolean) => void] {
  const [v, setV] = useState(collapsed);
  useEffect(() => {
    subs.add(setV);
    setV(collapsed); // re-sync in case it changed between render and effect
    return () => { subs.delete(setV); };
  }, []);
  return [v, setRailCollapsed];
}
